import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Filter, ChevronDown, ChevronUp, Edit3 } from 'lucide-react';
import { TarotCardFront } from '../components/TarotCard';
import api from '../lib/api';
import Navigation from '../components/Navigation';

export default function JournalPage() {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [reflectionText, setReflectionText] = useState('');
  const [savingReflection, setSavingReflection] = useState(false);

  useEffect(() => { loadReadings(); }, []);

  const loadReadings = async () => {
    try {
      const { data } = await api.get('/tarot/readings');
      setReadings(data.readings);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const saveReflection = async (readingId) => {
    if (!reflectionText.trim()) return;
    setSavingReflection(true);
    try {
      await api.post(`/tarot/reading/${readingId}/reflection`, { reflection_text: reflectionText });
      setReadings(prev => prev.map(r => r.id === readingId ? { ...r, reflection_notes: reflectionText } : r));
      setReflectionText('');
    } catch (e) {
      console.error(e);
    } finally {
      setSavingReflection(false);
    }
  };

  const filtered = filter === 'all' ? readings : readings.filter(r => r.intention === filter);
  const intentions = ['all', ...new Set(readings.map(r => r.intention))];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070A1A] flex items-center justify-center">
        <Navigation />
        <div className="w-8 h-8 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070A1A]" data-testid="journal-page">
      <Navigation />
      <div className="px-4 md:px-12 lg:px-24 py-8 pb-24 md:pb-8 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#FFFFF0]">My Journey</h1>
            <p className="text-sm text-[#BDB2C4] mt-1">{readings.length} readings recorded</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="journal-filters">
          {intentions.map(intent => (
            <button
              key={intent}
              onClick={() => setFilter(intent)}
              data-testid={`filter-${intent}`}
              className={`px-4 py-1.5 rounded-full text-xs tracking-wider transition-all duration-300 capitalize ${filter === intent ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40' : 'bg-white/5 text-[#BDB2C4] border border-white/10 hover:border-white/20'}`}
            >
              {intent}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <BookOpen size={32} className="text-[#BDB2C4] mx-auto mb-4" />
            <p className="text-[#BDB2C4]">No readings yet. Your journey begins with a single question.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((reading, idx) => (
              <motion.div
                key={reading.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="glass-card overflow-hidden"
                data-testid={`journal-entry-${idx}`}
              >
                <button
                  onClick={() => setExpandedId(expandedId === reading.id ? null : reading.id)}
                  className="w-full p-5 flex items-center justify-between text-left"
                >
                  <div>
                    <p className="text-sm text-[#FFFFF0] font-medium">{reading.question}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-[#D4AF37] capitalize">{reading.intention}</span>
                      <span className="text-xs text-[#BDB2C4]">{reading.spread_name}</span>
                      <span className="text-xs text-[#BDB2C4]">{new Date(reading.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {expandedId === reading.id ? <ChevronUp size={16} className="text-[#BDB2C4]" /> : <ChevronDown size={16} className="text-[#BDB2C4]" />}
                </button>

                {expandedId === reading.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="px-5 pb-5"
                  >
                    {/* Cards */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      {reading.cards?.map((card, i) => (
                        <div key={i} className="text-center">
                          <TarotCardFront card={card} position={reading.positions?.[i]} index={i} size="small" />
                        </div>
                      ))}
                    </div>

                    {/* Reading summary */}
                    {reading.reading?.unified_narrative && (
                      <div className="glass-card p-4 mb-4">
                        <p className="text-xs text-[#D4AF37] tracking-widest uppercase mb-2">The Story</p>
                        <p className="text-sm text-[#BDB2C4] leading-relaxed">{reading.reading.unified_narrative}</p>
                      </div>
                    )}

                    {/* Reflection */}
                    <div className="glass-card p-4 border-[#C87886]/10">
                      <p className="text-xs text-[#C87886] tracking-widest uppercase mb-2">
                        <Edit3 size={12} className="inline mr-1" /> Reflection Notes
                      </p>
                      {reading.reflection_notes ? (
                        <p className="text-sm text-[#BDB2C4] italic">{reading.reflection_notes}</p>
                      ) : (
                        <div>
                          <textarea
                            value={reflectionText}
                            onChange={e => setReflectionText(e.target.value)}
                            placeholder="Write your reflection here..."
                            className="w-full h-20 bg-transparent text-[#FFFFF0] placeholder:text-[#BDB2C4]/40 resize-none focus:outline-none text-sm"
                            data-testid={`reflection-input-${idx}`}
                          />
                          <button
                            onClick={() => saveReflection(reading.id)}
                            disabled={savingReflection}
                            className="text-xs text-[#D4AF37] hover:underline mt-1"
                            data-testid={`save-reflection-${idx}`}
                          >
                            {savingReflection ? 'Saving...' : 'Save Reflection'}
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
